import React from "react";
import { motion } from "framer-motion";
import { NavLink } from "react-router-dom";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6 },
  },
};

const fadeLeft = {
  hidden: { opacity: 0, x: -40 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6 },
  },
};

const fadeRight = {
  hidden: { opacity: 0, x: 40 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6 },
  },
};

const stagger = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.15 },
  },
};

const stats = [
  { value: "Security-First", label: "Delivery Mindset" },
  { value: "Business-Focused", label: "Technology Approach" },
  { value: "Practical", label: "Implementation Style" },
  { value: "Long-Term", label: "Client Value" },
];

const values = [
  {
    title: "Practical Security",
    desc: "We focus on improvements that work in real environments, not just theoretical best practices.",
    icon: "🛡️",
  },
  {
    title: "Operational Reliability",
    desc: "We build systems and workflows that support consistency, visibility, and dependable performance.",
    icon: "⚙️",
  },
  {
    title: "Business Alignment",
    desc: "We believe security should support business growth, not create unnecessary friction.",
    icon: "📈",
  },
  {
    title: "Continuous Improvement",
    desc: "Strong systems are built over time through review, refinement, and better operational discipline.",
    icon: "🚀",
  },
];

const strengths = [
  "Secure application architecture and implementation",
  "Business platform development with operational reliability",
  "API security and backend protection",
  "Data protection and controlled user access",
  "Workflow-oriented design for logistics and business systems",
  "Security improvements that balance control and usability",
];

const whyUs = [
  {
    title: "We Understand Real Business Systems",
    desc: "Our approach is grounded in how modern applications, operational platforms, users, and workflows actually function day to day.",
  },
  {
    title: "We Focus on Usable Security",
    desc: "We create security improvements that teams can realistically adopt, maintain, and scale.",
  },
  {
    title: "We Combine Technology with Operational Thinking",
    desc: "We look beyond code to understand processes, dependencies, access patterns, and business risks.",
  },
  {
    title: "We Build for Long-Term Stability",
    desc: "Our goal is not just to fix isolated issues, but to help create stronger digital foundations over time.",
  },
];

const About = () => {
  return (
    <>
      {/* HERO */}
      <section className="container-fluid tw-bg-gradient-to-r tw-from-slate-950 tw-via-slate-900 tw-to-slate-800 tw-text-white tw-py-5">
        <div className="container">
          <div className="row align-items-center tw-min-h-[75vh]">
            <motion.div
              className="col-lg-7 mb-5 mb-lg-0"
              initial="hidden"
              animate="visible"
              variants={stagger}
            >
              <motion.span
                variants={fadeUp}
                className="tw-inline-block tw-text-sm tw-uppercase tw-tracking-[0.25em] tw-text-cyan-300 tw-mb-3"
              >
                About Our Company
              </motion.span>

              <motion.h1
                className="tw-text-4xl md:tw-text-5xl tw-font-bold tw-leading-tight tw-mb-3"
                variants={fadeUp}
              >
                We Build Secure, Reliable, and Business-Ready Digital Solutions
              </motion.h1>

              <motion.p
                className="tw-text-slate-300 tw-text-lg tw-mb-4"
                variants={fadeUp}
              >
                We are a technology-focused team committed to helping businesses
                protect applications, strengthen systems, and operate with more
                confidence through practical cybersecurity and reliable digital
                solutions.
              </motion.p>

              <motion.div
                variants={fadeUp}
                className="tw-flex tw-flex-wrap tw-gap-3"
              >
                <NavLink to="/contact" className="btn btn-primary">
                  Contact Us
                </NavLink>
                <NavLink to="/services" className="btn btn-outline-light">
                  Explore Services
                </NavLink>
              </motion.div>
            </motion.div>

            <motion.div
              className="col-lg-5"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7 }}
            >
              <div className="tw-relative">
                <img
                  src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b"
                  alt="About company"
                  className="img-fluid tw-rounded-3xl tw-shadow-lg tw-w-full"
                />
                <div className="tw-absolute tw-bottom-4 tw-right-4 tw-bg-white tw-text-slate-900 tw-rounded-2xl tw-shadow-xl tw-p-4 tw-max-w-xs">
                  <h6 className="tw-font-bold tw-mb-1">Built on Trust</h6>
                  <p className="tw-text-sm tw-text-slate-600 tw-mb-0">
                    Stronger systems, safer workflows, and practical solutions
                    for modern business operations.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="container tw-py-5">
        <motion.div
          className="row text-center"
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {stats.map((item, i) => (
            <motion.div
              key={i}
              className="col-6 col-md-3 mb-4"
              variants={fadeUp}
            >
              <div className="tw-bg-white tw-rounded-3xl tw-p-4 tw-shadow-sm tw-border tw-border-slate-100">
                <h2 className="tw-text-2xl md:tw-text-3xl tw-font-bold tw-mb-1">
                  {item.value}
                </h2>
                <p className="tw-text-slate-600 tw-mb-0">{item.label}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* WHO WE ARE */}
      <section className="container tw-py-5">
        <div className="row align-items-center tw-min-h-[500px]">
          <motion.div
            className="col-lg-6 mb-4 mb-lg-0"
            variants={fadeLeft}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            <img
              src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f"
              alt="Who we are"
              className="img-fluid tw-rounded-3xl tw-shadow-lg tw-w-full"
            />
          </motion.div>

          <motion.div
            className="col-lg-6"
            variants={fadeRight}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            <span className="tw-inline-block tw-text-sm tw-uppercase tw-tracking-[0.2em] tw-text-cyan-600 tw-mb-2">
              Who We Are
            </span>

            <h2 className="tw-text-3xl tw-font-semibold tw-mb-3">
              A Team Focused on Security, Reliability, and Practical Execution
            </h2>

            <p className="tw-text-slate-600 tw-mb-3">
              We work at the intersection of technology, cybersecurity, and
              operations. Our focus is on helping businesses build and maintain
              digital systems that are secure, dependable, and aligned with
              real-world workflow needs.
            </p>

            <p className="tw-text-slate-600 tw-mb-4">
              Instead of offering generic solutions, we aim to understand how
              systems are actually used, where risks exist, and what
              improvements will create the most meaningful business value.
            </p>

            <ul className="tw-text-slate-700 tw-space-y-2 tw-pl-4">
              <li>• Secure software and platform thinking</li>
              <li>• Operationally practical implementation</li>
              <li>• Stronger control around users, systems, and data</li>
              <li>• Long-term focus on resilience and improvement</li>
            </ul>
          </motion.div>
        </div>
      </section>

      {/* MISSION / VISION */}
      <section className="container-fluid tw-bg-slate-50 tw-py-5">
        <div className="container">
          <motion.div
            className="row"
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.15 }}
          >
            <motion.div className="col-md-6 mb-4" variants={fadeUp}>
              <div className="tw-bg-white tw-p-5 tw-rounded-3xl tw-shadow-sm tw-border tw-border-slate-100 tw-h-full">
                <h4 className="tw-font-semibold tw-mb-3">Our Mission</h4>
                <p className="tw-text-slate-600 tw-mb-0">
                  To deliver practical, effective, and scalable digital security
                  solutions that protect business systems, data, and workflows
                  without unnecessary complexity.
                </p>
              </div>
            </motion.div>

            <motion.div className="col-md-6 mb-4" variants={fadeUp}>
              <div className="tw-bg-white tw-p-5 tw-rounded-3xl tw-shadow-sm tw-border tw-border-slate-100 tw-h-full">
                <h4 className="tw-font-semibold tw-mb-3">Our Vision</h4>
                <p className="tw-text-slate-600 tw-mb-0">
                  To help create digital environments where organizations can
                  operate with confidence through stronger security, greater
                  reliability, and better operational clarity.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* VALUES */}
      <section className="container tw-py-5">
        <div className="text-center mb-5">
          <h2 className="tw-text-3xl tw-font-semibold">What We Stand For</h2>
          <p className="tw-text-slate-600 tw-max-w-3xl tw-mx-auto">
            Our work is guided by principles that help us create solutions that
            are secure, usable, and genuinely valuable for business operations.
          </p>
        </div>

        <motion.div
          className="row"
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
        >
          {values.map((item, i) => (
            <motion.div key={i} className="col-md-6 col-lg-3 mb-4" variants={fadeUp}>
              <div className="tw-bg-white tw-p-4 tw-rounded-3xl tw-shadow-sm tw-border tw-border-slate-100 tw-h-full text-center">
                <div className="tw-text-4xl tw-mb-3">{item.icon}</div>
                <h5 className="tw-font-semibold tw-mb-2">{item.title}</h5>
                <p className="tw-text-slate-600 tw-text-sm tw-mb-0">{item.desc}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* EXPERTISE */}
      <section className="container-fluid tw-bg-slate-100 tw-py-5">
        <div className="container">
          <div className="row align-items-center">
            <motion.div
              className="col-lg-6 mb-4 mb-lg-0"
              variants={fadeLeft}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
            >
              <h2 className="tw-text-3xl tw-font-semibold tw-mb-3">
                Our Expertise
              </h2>

              <p className="tw-text-slate-600 tw-mb-4">
                We specialize in building and strengthening modern web
                applications, business platforms, and operational systems with a
                strong emphasis on security, performance, and reliability.
              </p>

              <ul className="tw-text-slate-700 tw-space-y-2 tw-pl-4">
                {strengths.map((item, i) => (
                  <li key={i}>• {item}</li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              className="col-lg-6"
              variants={fadeRight}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
            >
              <img
                src="https://images.unsplash.com/photo-1555949963-aa79dcee981c"
                alt="Expertise"
                className="img-fluid tw-rounded-3xl tw-shadow-lg tw-w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="container tw-py-5">
        <div className="text-center mb-5">
          <h2 className="tw-text-3xl tw-font-semibold">Why Businesses Choose Us</h2>
          <p className="tw-text-slate-600 tw-max-w-3xl tw-mx-auto">
            We focus on delivering solutions that make sense both technically
            and operationally.
          </p>
        </div>

        <motion.div
          className="row"
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
        >
          {whyUs.map((item, i) => (
            <motion.div key={i} className="col-md-6 mb-4" variants={fadeUp}>
              <div className="tw-bg-white tw-p-4 tw-rounded-3xl tw-shadow-sm tw-border tw-border-slate-100 tw-h-full">
                <h5 className="tw-font-semibold tw-mb-2">{item.title}</h5>
                <p className="tw-text-slate-600 tw-mb-0">{item.desc}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* CTA */}
      <section className="container-fluid tw-bg-gradient-to-r tw-from-slate-950 tw-to-slate-800 tw-text-white tw-py-5 text-center">
        <div className="container">
          <h2 className="tw-text-3xl tw-font-semibold">
            Let’s Build Secure and Reliable Systems Together
          </h2>
          <p className="tw-text-slate-300 tw-max-w-2xl tw-mx-auto">
            Connect with us to strengthen your applications, improve system
            reliability, and build a stronger foundation for long-term business
            growth.
          </p>

          <div className="tw-mt-4 tw-flex tw-justify-center tw-gap-3 tw-flex-wrap">
            <NavLink to="/contact" className="btn btn-primary">
              Contact Us
            </NavLink>
            <NavLink to="/services" className="btn btn-outline-light">
              Learn More
            </NavLink>
          </div>
        </div>
      </section>
    </>
  );
};

export default About;